import { useState, useEffect, useRef, useMemo, useContext } from "react";
import { useQuery } from "@apollo/client";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { getUrl } from "aws-amplify/storage";
import classes from "./ServiceModal.module.css";
import { createPortal } from "react-dom";
import { getCloudFrontImageUrl } from "../../util/cloudFrontUtils";
import awsConfig from "../../config/aws-config";
import { NavBarContext } from "../../App";
import { GET_PACKAGE_PROJECT_LINKS } from "../../graphql/mutations/creativeMutations";
import {
  buildBookingAuthNavigationState,
  buildStudioBookingRedirectLocation,
  storeStudioBookingRedirect,
} from "../../util/bookingAuthRedirect";
import { createPersistableBookingData } from "../../store/bookingStore";

export const ServiceModal = ({ open, onClose, service, creative, servicesArray, hideEditButton = false }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isPhoneSize } = useContext(NavBarContext);
  
  // Get current user from Redux
  const { user: currentUser, isAuthenticated } = useSelector((state) => state.auth || {});
  const currentUserId = currentUser?._id || currentUser?.id;
  
  // Check if current user is the owner of the creative
  const isOwner = currentUserId && creative?.userID && currentUserId === creative.userID;
  
  // Check if we're on a member page route (hide edit buttons on member pages)
  const isMemberPage = location.pathname.startsWith('/members/');
  const isEditingView = !isMemberPage && !hideEditButton && !!creative;
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartX, setDragStartX] = useState(0);
  const [dragOffset, setDragOffset] = useState(0);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const carouselRef = useRef(null);
  const audioRefs = useRef({});
  const [currentlyPlayingId, setCurrentlyPlayingId] = useState(null);

  // Fetch linked projects (Beispielarbeiten) for this package when modal is open
  const { data: packageLinksData } = useQuery(GET_PACKAGE_PROJECT_LINKS, {
    skip: !open || !service?.id,
    variables: { packageID: service?.id }
  });

  // Linked projects to show under Beispielarbeiten (from PackageProjectLink)
  const linkedProjectsList = useMemo(() => {
    const items = packageLinksData?.packageProjectLinksByPackageID?.items ?? [];
    return items.map((link) => link?.project).filter(Boolean);
  }, [packageLinksData]);

  // Process linked projects: images and audio cover same way as useCreative (CloudFront + S3 fallback)
  const processedLinkedProjects = useMemo(() => {
    if (!linkedProjectsList.length) return [];
    return linkedProjectsList.map((project) => {
      const processed = { ...project };
      const img = project?.images?.[0];
      const audio = project?.audios?.[0];

      // Cover/display image: prefer project image, then audio cover (same order as MusicProjectModal)
      const rawImageKey = img?.thumbnailUrl || img?.url || audio?.coverImage || "";
      if (rawImageKey) {
        try {
          processed.processedCoverImage = getCloudFrontImageUrl(rawImageKey, {
            width: 600,
            height: 600,
            fit: "cover"
          });
        } catch (error) {
          try {
            const cleanKey = rawImageKey.startsWith("public/")
              ? rawImageKey
              : `public/${rawImageKey}`;
            processed.processedCoverImage = `https://${awsConfig.Storage.S3.bucket}.s3.${awsConfig.Storage.S3.region}.amazonaws.com/${cleanKey}`;
          } catch (fallbackError) {
            processed.processedCoverImage = rawImageKey;
          }
        }
      }

      // Normalize audio key for getUrl (same as useCreative / MusicProjectModal)
      if (audio?.url) {
        let cleanKey = audio.url;
        while (cleanKey.startsWith("public/public/")) {
          cleanKey = cleanKey.replace("public/public/", "public/");
        }
        if (!cleanKey.startsWith("public/")) {
          cleanKey = `public/${cleanKey}`;
        }
        processed.audios = [
          {
            ...audio,
            audioKey: cleanKey
          }
        ];
      }
      return processed;
    });
  }, [linkedProjectsList]);

  // Resolve audio URLs with getUrl (same as MusicProjectModal) so playback works correctly
  const [resolvedAudioUrls, setResolvedAudioUrls] = useState({});
  useEffect(() => {
    if (!open || !processedLinkedProjects.length) {
      setResolvedAudioUrls({});
      return;
    }
    let cancelled = false;
    const resolve = async () => {
      const next = {};
      for (const project of processedLinkedProjects) {
        const audio = project?.audios?.[0];
        const audioKey = audio?.audioKey || audio?.url;
        if (!audioKey) continue;
        try {
          let keyForGetUrl = audioKey;
          if (keyForGetUrl.startsWith("public/")) {
            keyForGetUrl = keyForGetUrl.replace("public/", "");
          }
          const result = await getUrl({
            key: keyForGetUrl,
            options: { accessLevel: "public", validateObjectExistence: false }
          });
          if (!cancelled) next[project.id] = result.url.toString();
        } catch (error) {
          try {
            const cleanKey = audioKey.startsWith("public/") ? audioKey : `public/${audioKey}`;
            next[project.id] = `https://${awsConfig.Storage.S3.bucket}.s3.${awsConfig.Storage.S3.region}.amazonaws.com/${cleanKey}`;
          } catch (e) {
            next[project.id] = audioKey;
          }
        }
      }
      if (!cancelled) setResolvedAudioUrls(next);
    };
    resolve();
    return () => { cancelled = true; };
  }, [open, processedLinkedProjects]);

  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  useEffect(() => {
    if (service && servicesArray) {
      const index = servicesArray.findIndex((p) => p.id === service.id);
      if (index !== -1) {
        setCurrentIndex(index);
      }
    }
  }, [service, servicesArray]);

  // Reset image index when service changes
  useEffect(() => {
    if (service?.processedPortfolioImages) {
      setCurrentImageIndex(0);
    }
  }, [service]);

  // Process media items similar to ProjectOverview - detect videos and handle thumbnails
  const mediaItems = useMemo(() => {
    if (!service) return [];
    
    const portfolioImages = service.portfolioImages || [];
    const processedPortfolioImages = service.processedPortfolioImages || [];
    
    return portfolioImages.map((imageKey, index) => {
      const processedUrl = processedPortfolioImages[index] || '';
      
      // Check if it's a link (YouTube/Vimeo)
      const isLink = imageKey && typeof imageKey === 'string' && 
        (imageKey.includes('youtube.com') || imageKey.includes('youtu.be') || imageKey.includes('vimeo.com'));
      
      // Check if it's a video - check file extension
      const isVideo = imageKey && typeof imageKey === 'string' && 
        /\.(mp4|mov|avi|wmv|flv|webm|mkv|m4v)$/i.test(imageKey);
      
      const isPhoto = !isLink && !isVideo;
      
      // For videos and links, we need to handle thumbnails
      let displayUrl = '';
      let videoUrl = null;
      
      if (isLink) {
        // For links, use processed URL or try to get thumbnail
        displayUrl = processedUrl || '';
      } else if (isVideo) {
        // For videos, use processed URL for display (which should be thumbnail if processed correctly)
        // The processed URL might be the thumbnail, or we need to construct it
        displayUrl = processedUrl || '';
        
        // Video file URL - construct S3 URL
        if (imageKey && !imageKey.startsWith('http')) {
          const cleanKey = imageKey.startsWith('public/') ? imageKey : `public/${imageKey}`;
          videoUrl = `https://${awsConfig.Storage.S3.bucket}.s3.${awsConfig.Storage.S3.region}.amazonaws.com/${cleanKey}`;
        } else {
          videoUrl = imageKey;
        }
      } else {
        // For photos, use processed URL
        displayUrl = processedUrl || imageKey || '';
      }
      
      return {
        url: imageKey,
        processedUrl: processedUrl,
        displayUrl: displayUrl,
        videoUrl: videoUrl,
        isLink: isLink,
        isVideo: isVideo,
        isPhoto: isPhoto
      };
    }).filter(item => item.displayUrl || item.isLink || item.isVideo);
  }, [service]);

  // For MUSIC creative, show only one image in the thumbnail strip
  const displayMediaItems = useMemo(() => {
    if (creative?.creativeType === "MUSIC" && mediaItems.length > 0) {
      return mediaItems.slice(0, 1);
    }
    return mediaItems;
  }, [creative?.creativeType, mediaItems]);

  // Debug: log linked projects data coming from backend
  useEffect(() => {
    if (!service?.id) return;
    if (!packageLinksData) return;

  }, [service?.id, packageLinksData, linkedProjectsList]);

  const images = mediaItems.map(item => item.displayUrl).filter(url => url);

  // Carousel drag handlers
  const handleMouseDown = (e) => {
    if (!mediaItems?.length) return;
    setIsDragging(true);
    setDragStartX(e.clientX);
    setDragOffset(0);
    e.preventDefault();
  };

  const handleMouseMove = (e) => {
    if (!isDragging || !mediaItems?.length) return;
    const currentX = e.clientX;
    const diff = currentX - dragStartX;
    const container = carouselRef.current;
    const containerWidth = container ? container.offsetWidth : 1;
    const offsetPercent = (diff / containerWidth) * 100;
    let constrained = offsetPercent;
    if (currentImageIndex === 0 && offsetPercent > 0) constrained = 0;
    if (currentImageIndex === mediaItems?.length - 1 && offsetPercent < 0) constrained = 0;
    setDragOffset(constrained);
  };

  const commitDrag = () => {
    if (!isDragging || !mediaItems?.length) return;
    const threshold = 5;
    const dragDistance = Math.abs(dragOffset);
    if (dragDistance > threshold) {
      if (dragOffset > 0 && currentImageIndex > 0) {
        setCurrentImageIndex(currentImageIndex - 1);
      } else if (dragOffset < 0 && currentImageIndex < mediaItems?.length - 1) {
        setCurrentImageIndex(currentImageIndex + 1);
      }
    }
    setIsDragging(false);
    setDragOffset(0);
  };

  const handleMouseUp = () => commitDrag();
  const handleMouseLeave = () => { if (isDragging) commitDrag(); };

  const handleTouchStart = (e) => {
    if (!mediaItems?.length) return;
    const touch = e.touches[0];
    setIsDragging(true);
    setDragStartX(touch.clientX);
    setDragOffset(0);
  };

  const translateX = (() => {
    const base = -100 * currentImageIndex;
    return isDragging ? base + dragOffset : base;
  })();

  const handleTouchMove = (e) => {
    if (!isDragging) return;
    if (e.cancelable) e.preventDefault();
    const touch = e.touches[0];
    const currentX = touch.clientX;
    const diff = currentX - dragStartX;
    const container = carouselRef.current;
    const containerWidth = container ? container.offsetWidth : 1;
    const offsetPercent = (diff / containerWidth) * 100;
    let constrained = offsetPercent;
    if (currentImageIndex === 0 && offsetPercent > 0) constrained = 0;
    if (currentImageIndex === mediaItems?.length - 1 && offsetPercent < 0) constrained = 0;
    setDragOffset(constrained);
  };

  const handleTouchEnd = () => commitDrag();

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  const handleClose = () => {
    // Stop any playing audio when closing
    if (currentlyPlayingId && audioRefs.current[currentlyPlayingId]) {
      const audioEl = audioRefs.current[currentlyPlayingId];
      try {
        audioEl.pause();
        audioEl.currentTime = 0;
      } catch (e) {
        // ignore
      }
    }
    setCurrentlyPlayingId(null);
    onClose();
  };

  const handleTogglePlay = (projectId, resolvedAudioUrl) => {
    if (!projectId || !resolvedAudioUrl) return;

    const audioEl = audioRefs.current[projectId];
    if (!audioEl) return;

    if (!audioEl.src || !audioEl.src.includes(resolvedAudioUrl)) {
      audioEl.src = resolvedAudioUrl;
    }

    // If this project is currently playing -> stop and rewind
    if (currentlyPlayingId === projectId && !audioEl.paused) {
      audioEl.pause();
      audioEl.currentTime = 0;
      setCurrentlyPlayingId(null);
      return;
    }

    // Stop any previously playing audio
    if (currentlyPlayingId && audioRefs.current[currentlyPlayingId]) {
      const prev = audioRefs.current[currentlyPlayingId];
      try {
        prev.pause();
        prev.currentTime = 0;
      } catch (e) {
        // ignore
      }
    }

    audioEl.currentTime = 0;
    audioEl
      .play()
      .then(() => {
        setCurrentlyPlayingId(projectId);
      })
      .catch(() => {
        // ignore play errors (e.g. autoplay blocked)
      });
  };

  const handleMitbuchenClick = () => {
    if (!creative || !service) return;

    // Get studio ID from creative
    const studioId = creative?.studios?.items?.[0]?.studioID;
    if (!studioId) return;

    // Prepare the package to pre-select
    const bookedPackage = {
      creativeId: creative.id,
      packageId: service.id,
      duration: service.duration || 1,
      price: service.price || service.pricePerHour || 0,
      specialRequests: '',
    };

    // Update booking data in localStorage
    const storageKey = `studioBookingData_${studioId}`;
    const savedData = localStorage.getItem(storageKey);
    let bookingData = {
      studioId,
      date: '',
      startTime: '',
      endTime: '',
      addFreelancer: true,
      capacity: '',
      equipment: [],
      totalStudioPrice: 0,
      totalCreativePrice: 0,
      totalPrice: 0,
      projectName: '',
      companyName: '',
      description: '',
      creativePackages: [bookedPackage],
    };

    if (savedData) {
      try {
        const parsed = JSON.parse(savedData);
        if (parsed.data && Date.now() - parsed.timestamp < 30 * 60 * 1000) {
          // Merge with existing data, but ensure addFreelancer is true and package is added
          const existingPackages = parsed.data.creativePackages || [];
          // Check if package already exists
          const packageExists = existingPackages.some(p => p.packageId === service.id);
          bookingData = {
            ...parsed.data,
            addFreelancer: true,
            creativePackages: packageExists ? existingPackages : [...existingPackages, bookedPackage],
          };
        }
      } catch (e) {
        // Use default bookingData
      }
    }

    // Save to localStorage
    localStorage.setItem(storageKey, JSON.stringify({
      data: createPersistableBookingData(bookingData),
      timestamp: Date.now()
    }));

    const bookingTarget = buildStudioBookingRedirectLocation({ studioId, isPhoneSize });

    // Close the modal before navigation/login so no overlay remains stuck behind auth UI.
    onClose();

    if (!isAuthenticated && !currentUserId) {
      storeStudioBookingRedirect({ studioId, isPhoneSize });
      const state = buildBookingAuthNavigationState({ location, from: bookingTarget });
      navigate(isPhoneSize ? '/anmelden' : '/', { state });
      return;
    }

    navigate(bookingTarget);
  };

  if (!open || !service) return null;

  return createPortal(
    <>
      <div className={classes.backdrop} onClick={handleBackdropClick} />
      <div className={classes.modal} onClick={handleBackdropClick}>
        <div className={classes.modalWrapper}>
          <div className={classes.modalContent} onClick={(e) => e.stopPropagation()}>
            <button className={classes.closeButton} onClick={handleClose}>
              <svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 17 17" fill="none">
                <path fillRule="evenodd" clipRule="evenodd" d="M7.50947 8.5L0 0.990535L0.990535 0L8.5 7.50947L16.0095 0L17 0.990535L9.49054 8.5L17 16.0095L16.0095 17L8.5 9.49054L0.990535 17L0 16.0095L7.50947 8.5Z" fill="#1F1F1F"/>
              </svg>
            </button>

            {/* Phone Header */}
            <header className={classes.phoneHeader}>
              <button className={classes.phoneBackButton} onClick={handleClose}>
                <svg xmlns="http://www.w3.org/2000/svg" width="11" height="20" viewBox="0 0 11 20" fill="none">
                  <path fillRule="evenodd" clipRule="evenodd" d="M0 9.6255L8.99855 0.626953L10.2006 1.82903L2.35773 9.67193L10.2486 18.2204L8.99941 19.3734L0.0172915 9.64279L0 9.6255Z" fill="#1F1F1F"/>
                </svg>
              </button>
              {isEditingView && (
                <Link 
                  to={`/creative-editing/${creative.id}/package/${service.id}`}
                  className={classes.phoneEditButton}
                >
                  <span className="mediumLarge">Bearbeiten</span>
                </Link>
              )}
            </header>

            {/* Phone Image Carousel */}
            {mediaItems.length > 0 && (
              <div 
                className={`${classes.imageCarousel} ${isEditingView ? classes.imageCarouselEditing : ''} ${isDragging ? classes.dragging : ''}`}
                ref={carouselRef}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseLeave}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
                style={{ '--translate-x': `${translateX}%` }}
              >
                <div 
                  className={`${classes.carouselTrack} ${isDragging ? classes.noTransition : ''}`}
                >
                  {mediaItems.map((item, index) => (
                    <div key={`slide-${index}`} className={classes.carouselSlide}>
                      {item.isLink ? (
                        <div className={classes.linkPlaceholder}>
                          <span>🔗</span>
                          <p>Link</p>
                        </div>
                      ) : item.isVideo ? (
                        item.displayUrl ? (
                          <img
                            src={item.displayUrl}
                            alt={`${service.name} ${index + 1}`}
                            className={classes.carouselImage}
                            loading={index === 0 ? 'eager' : 'lazy'}
                            decoding="async"
                            fetchpriority={index === 0 ? 'high' : 'auto'}
                          />
                        ) : (
                          <div className={classes.videoPlaceholder}>
                            <span>🎥</span>
                            <p>Video</p>
                          </div>
                        )
                      ) : item.displayUrl ? (
                        <img
                          src={item.displayUrl}
                          alt={`${service.name} ${index + 1}`}
                          className={classes.carouselImage}
                          loading={index === 0 ? 'eager' : 'lazy'}
                          decoding="async"
                          fetchpriority={index === 0 ? 'high' : 'auto'}
                        />
                      ) : null}
                    </div>
                  ))}
                </div>
                {mediaItems.length > 1 && (
                  <div className={`${classes.imageCounter} slimRegularMiniText`}>
                    {currentImageIndex + 1} / {mediaItems.length}
                  </div>
                )}
                {!isEditingView && (
                  <button onClick={handleClose} className={classes.closeButtonPhone}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 17 17" fill="none">
                      <path fillRule="evenodd" clipRule="evenodd" d="M7.50947 8.5L0 0.990535L0.990535 0L8.5 7.50947L16.0095 0L17 0.990535L9.49054 8.5L17 16.0095L16.0095 17L8.5 9.49054L0.990535 17L0 16.0095L7.50947 8.5Z" fill="white"/>
                    </svg>
                  </button>
                )}
              </div>
            )}

            <div className={classes.modalBody}>
              <div className={`${classes.servicesImageWrapper} ${creative?.creativeType === "MUSIC" ? classes.musicImageWrapper : ""}`}>
                {displayMediaItems.map((item, index) => (
                  <div
                    className={classes.serviceImageContainer}
                    key={index}
                    style={
                      creative?.creativeType === "MUSIC"
                        ? { width: "100%", height: "100%" }
                        : undefined
                    }
                  >
                    {item.isLink && (
                      <div className={classes.linkBadge}>
                        <span>🔗</span>
                      </div>
                    )}
                    {item.isVideo && !item.isLink && (
                      <div className={classes.videoBadge}>
                        <span>🎥</span>
                      </div>
                    )}
                    {item.displayUrl ? (
                      <img src={item.displayUrl} alt={service.name} />
                    ) : item.isVideo ? (
                      <div className={classes.videoPlaceholder}>
                        <span>🎥</span>
                        <p>Video</p>
                      </div>
                    ) : null}
                  </div>
                ))}
              </div>
              <div className={classes.serviceDescription}>
                <div className={classes.serviceInfos}>
                  <div className={classes.serviceInfosHeader}>
                    <h3 className="titleSemibold">{service?.name}</h3>
                    <p className="mediumLarge">{service?.price}€ pro Stunde</p>
                  </div>
                  <div className={classes.serviceInfosDescription}>
                    <p className="regularMiddle">{service?.description}</p>
                    <div className={classes.serviceInfosDeliverables}>
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="16" viewBox="0 0 24 16" fill="none">
                        <g clipPath="url(#clip0_16532_23385)">
                        <path d="M3.42188 7.83781L5.33187 5.87781C5.56187 5.63781 5.87187 5.50781 6.18187 5.50781H9.11187C9.31187 5.50781 9.51188 5.55781 9.68188 5.65781L11.1819 6.51781C11.3619 6.61781 11.5519 6.66781 11.7519 6.66781H14.9519C15.6519 6.66781 16.2219 7.30781 16.2219 8.10781C16.2219 8.90781 15.6519 9.54781 14.9519 9.54781H9.71187" stroke="#CB1541" strokeWidth="1.2" strokeMiterlimit="10"/>
                        <path d="M16.3219 7.79909L18.3619 6.35909C18.5519 6.24909 18.7119 6.08909 18.8219 5.87909L19.7319 4.00909C19.8819 3.71909 20.1319 3.50909 20.4319 3.42909L21.2219 3.20909C21.5019 3.12909 21.7919 3.17909 22.0419 3.34909C22.6119 3.71909 22.7719 4.56909 22.3819 5.16909L19.0319 10.4291C18.9619 10.5291 18.8819 10.6191 18.7919 10.6991L16.6519 12.5091C16.4719 12.6691 16.2419 12.7491 16.0119 12.7591L7.92188 13.0191" stroke="#CB1541" strokeWidth="1.2" strokeMiterlimit="10"/>
                        <path d="M3.42094 7.80898L7.84094 12.679C8.17094 13.049 8.14094 13.619 7.78094 13.949L6.33094 15.269L0.710938 9.06898L2.16094 7.74898C2.53094 7.41898 3.09094 7.43898 3.43094 7.80898H3.42094Z" stroke="#CB1541" strokeWidth="1.2" strokeMiterlimit="10"/>
                        <path d="M7.6875 3.57V0.92C7.6875 0.69 7.9775 0.5 8.3375 0.5H16.8675C17.2275 0.5 17.5175 0.69 17.5175 0.92V3.57" stroke="#CB1541" strokeWidth="1.2" strokeMiterlimit="10"/>
                        </g>
                        <defs>
                        <clipPath id="clip0_16532_23385">
                        <rect width="23.09" height="15.98" fill="white"/>
                        </clipPath>
                        </defs>
                      </svg>
                      {service?.deliverables?.map((item, index) => (
                        <span className="regularMiddle" key={index}>{item}</span>
                      ))}
                    </div>
                    <div className={classes.serviceInfosDeliveryTime}>
                      <svg xmlns="http://www.w3.org/2000/svg" width="19" height="19" viewBox="0 0 19 19" fill="none">
                        <path d="M17.5 9.34961C17.5 4.84849 13.8507 1.19922 9.34961 1.19922C4.84849 1.19922 1.19922 4.84849 1.19922 9.34961C1.19922 13.8507 4.84849 17.5 9.34961 17.5C13.8507 17.5 17.5 13.8507 17.5 9.34961ZM9.58594 4.09766V9.74707L13.7178 11.9707L13.1494 13.0273L8.48242 10.5176L8.38574 10.457V10.3975V4.09766H9.58594ZM18.6992 9.34961C18.6992 14.5135 14.5135 18.6992 9.34961 18.6992C4.18575 18.6992 0 14.5135 0 9.34961C0 4.18575 4.18575 0 9.34961 0C14.5135 0 18.6992 4.18575 18.6992 9.34961Z" fill="#CB1541"/>
                      </svg>
                      <span className="regularMiddle">{service?.deliveryTime}</span>
                    </div>
                  </div>
                  {/* Beispielarbeiten – linked projects (PackageProjectLink) */}
                  {(linkedProjectsList.length > 0 || (service?.exampleWorks && service.exampleWorks.length > 0)) && (
                    <div className={classes.serviceInfosExampleWork}>
                      <h3 className="mediumLarge">Beispielarbeiten</h3>
                      <div className={classes.serviceInfosExampleWorkItems}>
                        {processedLinkedProjects.length > 0
                          ? processedLinkedProjects.map((project) => {
                              const displayUrl = project.processedCoverImage || "";
                              const hasAudio = project?.audios?.[0];
                              const resolvedUrl = resolvedAudioUrls[project.id];
                              const isPlaying = currentlyPlayingId === project.id;
                              return (
                                <div className={classes.serviceInfosExampleWorkItem} key={project.id}>
                                  <div className={classes.serviceInfosExampleWorkItemImage}>
                                    {displayUrl ? (
                                      <img src={displayUrl} alt={project.title} />
                                    ) : (
                                      <div className={classes.videoPlaceholder} style={{ minHeight: 80 }}>
                                        <span>🖼</span>
                                      </div>
                                    )}
                                    {hasAudio && (
                                      <>
                                        <audio
                                          ref={(el) => {
                                            if (el) {
                                              audioRefs.current[project.id] = el;
                                              el.onended = () => {
                                                setCurrentlyPlayingId((prev) =>
                                                  prev === project.id ? null : prev
                                                );
                                              };
                                            }
                                          }}
                                          style={{ display: "none" }}
                                        />
                                        <button
                                          type="button"
                                          className={classes.playIcon}
                                          disabled={!resolvedUrl}
                                          onClick={() => resolvedUrl && handleTogglePlay(project.id, resolvedUrl)}
                                        >
                                          {isPlaying ? (
                                            <svg
                                              xmlns="http://www.w3.org/2000/svg"
                                              width="12"
                                              height="14"
                                              viewBox="0 0 12 14"
                                              fill="none"
                                            >
                                              <rect x="1" y="1" width="4" height="12" fill="white" />
                                              <rect x="7" y="1" width="4" height="12" fill="white" />
                                            </svg>
                                          ) : (
                                            <svg
                                              xmlns="http://www.w3.org/2000/svg"
                                              width="12"
                                              height="14"
                                              viewBox="0 0 12 14"
                                              fill="none"
                                            >
                                              <path
                                                d="M10.5821 5.72077C11.2539 6.13376 11.2539 7.16624 10.5821 7.57923L1.51397 13.1546C0.84225 13.5676 0.00260448 13.0514 0.00260448 12.2254V1.07459C0.00260448 0.248601 0.842251 -0.26764 1.51397 0.145354L10.5821 5.72077Z"
                                                fill="white"
                                              />
                                            </svg>
                                          )}
                                        </button>
                                      </>
                                    )}
                                  </div>
                                  <div className={classes.serviceInfosExampleWorkItemText}>
                                    <span className="titleSemibold3">{project.title}</span>
                                  </div>
                                </div>
                              );
                            })
                          : service?.exampleWorks?.map((item, index) => (
                              <div className={classes.serviceInfosExampleWorkItem} key={index}>
                                <div className={classes.serviceInfosExampleWorkItemImage}>
                                  <img src={item.image} alt={item.name} />
                                  <div className={classes.playIcon}>
                                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="14" viewBox="0 0 12 14" fill="none">
                                      <path d="M10.5821 5.72077C11.2539 6.13376 11.2539 7.16624 10.5821 7.57923L1.51397 13.1546C0.84225 13.5676 0.00260448 13.0514 0.00260448 12.2254V1.07459C0.00260448 0.248601 0.842251 -0.26764 1.51397 0.145354L10.5821 5.72077Z" fill="white"/>
                                    </svg>
                                  </div>
                                </div>
                                <div className={classes.serviceInfosExampleWorkItemText}>
                                  <span className="titleSemibold3">{item.name}</span>
                                  <span className="regularMiddle" style={{ color: "var(--dark-grey-3)" }}>
                                    {item.description}
                                  </span>
                                </div>
                              </div>
                            ))}
                      </div>
                    </div>
                  )}
                </div>
                <div className={classes.buttonsContainer}>
                  {isMemberPage && !isOwner && (
                    <button 
                      onClick={handleMitbuchenClick}
                      className={`${classes.mitbuchenButton} mediumLarge`}
                    >
                      Mitbuchen
                    </button>
                  )}
                  {isEditingView && (
                    <Link to={`/creative-editing/${creative.id}/package/${service.id}`} className={classes.serviceEditButton}>
                      <button className={`${classes.editButton} mediumLarge`}>Bearbeiten</button>
                    </Link>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>,
    document.getElementById("modal") || document.body
  );
};

export default ServiceModal;
